import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";

import { Select } from "../../components/inputs/select";
import { TableBody, TableHeader, TableHeaderItem, TableWrapper } from "../../components/table";
import { GET_WITHDRAW_HISTORY } from "../../components/graphql";
import { WithdrawHistoryItem, WithdrawHistoryItemSkeleton } from "./withdrawHistoryitem";

interface StateObject {
	withdrawType: string
	searchKey: string
	sortKey: string
}

const initState: StateObject = {
	withdrawType: "all",
	searchKey: "",
	sortKey: "",
}

const statusTypes: SelectitemData[] = [
	{
		value: "all",
		label: "All",
	}, {
		value: "pending",
		label: "Pending"
	}, {
		value: "successed",
		label: "Successed"
	}, {
		value: "rejected",
		label: "rejected"
	}
]

const emptyArray = new Array(5).fill(0);
const Withdraw = () => {
	const [state, setState] = useState<StateObject>(initState);

	const updateState = (data: Partial<StateObject>) => {
		setState({ ...state, ...data });
	}

	const { data: resData, loading, error } = useQuery(GET_WITHDRAW_HISTORY, {
		pollInterval: 2000,
	})

	const sortBy = useCallback((a: WithdrawHistoryObject, b: WithdrawHistoryObject) => {
		let res: boolean = true;

		switch (state.sortKey) {
			case "CurrencyDESC":
				res = a.currency.toUpperCase() > b.currency.toUpperCase();
				break;

			case "CurrencyASC":
				res = a.currency.toUpperCase() < b.currency.toUpperCase();
				break;

			case "AmountDESC":
				res = a.amount > b.amount;
				break;

			case "AmountASC":
				res = a.amount < b.amount;
				break;

			case "StatusDESC":
				res = a.status < b.status;
				break;

			case "StatusASC":
				res = a.status > b.status;
				break;

			case "TimeDESC":
				res = a.created < b.created;
				break;

			case "TimeASC":
				res = a.created > b.created;
				break;
		}

		return res ? 1 : -1;
	}, [state.sortKey])

	const withdrawHistorys: WithdrawHistoryObject[] = useMemo(() => {
		if (!!loading || !!error) return [];
		return resData.getUserWithdrawHistory.filter(({ status }: WithdrawHistoryObject) => (
			status.toLowerCase() === state.withdrawType.toLowerCase() || state.withdrawType === "all"
		)).sort(sortBy)
	}, [resData, loading, error, state.withdrawType, sortBy])

	const onChangeValue = (key: string) => (value: string) => {
		updateState({ [key]: value })
	}

	const onChangeWithdrawType = ({ target }: any) => {
		updateState({ withdrawType: target.value })
	}

	return (
		<WithdrawWrapper>
			<Typography variant="h3">
				Withdrawal History
			</Typography>

			<Typography variant="body1" component="h6">
				The Withdrawal History section displays user withdrawal requests, their current status, and past transactions, ensuring clear and transparent payout tracking.
			</Typography>

			<SearchContainer>
				<Select value={state.withdrawType}
					label="WithdrawType" name="withdrawType"
					onChange={onChangeWithdrawType}
					className="select-wrapper"
					items={statusTypes}
				/>
			</SearchContainer>

			<TableWrapper>
				<TableHeader>
					<TableHeaderItem first title="Currency" sortKey={state.sortKey} onChange={onChangeValue("sortKey")} />
					<TableHeaderItem title="Amount" sortKey={state.sortKey} onChange={onChangeValue("sortKey")} />
					<TableHeaderItem title="Status" sortKey={state.sortKey} onChange={onChangeValue("sortKey")} />
					<TableHeaderItem title="Time" sortKey={state.sortKey} onChange={onChangeValue("sortKey")} />
				</TableHeader>

				{!loading && (
					<TableBody>
						{withdrawHistorys.map((history: WithdrawHistoryObject, key: number) => (
							<WithdrawHistoryItem key={key}
								index={key + 1} data={history}
							/>
						))}
					</TableBody>
				)}

				{loading && (
					<TableBody>
						{emptyArray.map((i, key: number) => (
							<WithdrawHistoryItemSkeleton index={key + 1} key={key} />
						))}
					</TableBody>
				)}
			</TableWrapper>
		</WithdrawWrapper>
	)
}

const WithdrawWrapper = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const SearchContainer = styled(Box)(({ theme }) => ({
	flexWrap: "wrap",
	display: "flex",
	flexDirection: "column",

	".select-wrapper": {
		maxWidth: 350,
	},

	[theme.breakpoints.down("md")]: {
		".select-wrapper": {
			maxWidth: "unset",
		},
	}
}))

export { Withdraw }